import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  CardActionArea,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { EthiquinEmptyState } from "../../../components/EthiquinEmptyState";
import EGFileUpload from "../../../../../../MainComponents/FileUpload/EGFileUpload/EGFileUpload";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { EGBasicSelect } from "../../../../../../MainComponents/Select/EGBasicSelect";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { createAttachment, updateInterview } from "../../../../../../../apis";
import { useConstants } from "../../../Main/useConstants";
import {
  ChartSquare,
  Folder,
  Gallery,
  OutlineDocument,
  OutlineDocumentPDF,
  OutlineDocumentText,
  OutlineDocumentText2,
  Paperclip,
  PPTX,
  TrashCan,
  VideoSquare,
  VolumeHigh,
} from "../../../../../../Icon";
import {
  detectFileType,
  getIconKey,
} from "../../../../../../../helpers/typeFile";
import { AudioPlayer } from "./AudioPlayer";

export const Evidence = forwardRef(
  (
    {
      evidence = [],
      id,
      setInterviews,
      isLoading,
      setIsLoading,
      alias,
      readOnly = false,
    },
    ref
  ) => {
    const theme = useTheme();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const objectUrlMap = useRef(new Map());
    const [t] = useTranslation("investigation");
    const isXs = useMediaQuery(theme.breakpoints.down("sm")); // <600
    const isSm = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600-900
    // decide cols: 1 for xs, 2 for sm, 3 for md+
    const cols = useMemo(() => (isXs ? 1 : isSm ? 2 : 3), [isXs, isSm]);
    const {
      interviews: { typeFileOpts },
    } = useConstants();
    const purposeLabel = useCallback(
      (value) => typeFileOpts.find((opt) => opt.value === value)?.label || "",
      [typeFileOpts]
    );
    
    // --- Initial States --- //
    // New Files
    const [evidenceFiles, setEvidenceFiles] = useState([]);
    const [openAttachmentUploader, setOpenAttachmentUploader] = useState(false);
    // View Files
    const [openView, setOpenView] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [activeElement, setActiveElement] = useState(null);
    // Options Files
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuIndex, setMenuIndex] = useState(null);
    const [selectedElement, setSelectedElement] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);

    // ---- Upload file ---- //
    // cleanup when dismantling: revokes all URLs in the map
    useEffect(() => {
      return () => {
        objectUrlMap.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlMap.current.clear();
      };
    }, []);
    // Open file uploader
    const addNewFile = () => {
      setOpenAttachmentUploader(true);
    };
    // Close & clean file uploader
    const closeAddNewFile = () => {
      setOpenAttachmentUploader(false);
      setEvidenceFiles([]);
    };
    // Remove file before sending
    const removeFile = (fileName) => {
      const updated = evidenceFiles.filter((entry) => {
        const f = extractFile(entry);
        return f.name !== fileName;
      });
      setEvidenceFiles(updated);
    };

    // Normalize entries so each carries an optional purpose
    const normalizeFiles = useCallback((arr) => {
      if (!Array.isArray(arr)) return [];
      return arr.map((entry) => {
        const isWrapped = entry && typeof entry === "object" && "file" in entry;
        const fileEntry = isWrapped ? entry : { file: entry };
        return {
          ...fileEntry,
          purpose: fileEntry.purpose ?? "",
        };
      });
    }, []);

    const handleSetFilesFromUpload = useCallback(
      (incoming) => {
        if (typeof incoming === "function") {
          setEvidenceFiles((prev) => normalizeFiles(incoming(prev)));
        } else {
          setEvidenceFiles(normalizeFiles(incoming));
        }
      },
      [normalizeFiles]
    );

    const updateFilePurpose = useCallback((fileName, value) => {
      setEvidenceFiles((prev) =>
        prev.map((entry) => {
          const f = extractFile(entry);
          if (f?.name === fileName) {
            return { ...entry, purpose: value };
          }
          return entry;
        })
      );
    }, []);

    // OnSubmit
    const handleSubmit = async () => {
      if (!evidenceFiles.length) return;
      const saving = enqueueSnackbar(`${t("Interview.saving")}...`, {
        variant: "info",
        persist: true,
      });
      setIsLoading(true);
      try {
        // Upload all new files
        const uploadPromises = evidenceFiles.map(async (entry) => {
          const file = extractFile(entry);
          const fd =
            entry && entry.formData
              ? entry.formData
              : (() => {
                  const newFd = new FormData();
                  newFd.append("attachments", file);
                  return newFd;
                })();
          // Get the save ID
          const resp = await createAttachment(fd);
          const fileId = resp.attachments_id;
          return {
            attachments_file: fileId,
            attachments_data: {
              name: file.name,
              size: file.size,
              type: file.type,
              purpose: entry.purpose || "other",
            },
          };
        });

        // Once the await is settled
        const settled = await Promise.allSettled(uploadPromises);
        const successes = settled
          .filter((s) => s.status === "fulfilled")
          .map((s) => s.value);
        const failures = settled.filter((s) => s.status === "rejected");
        // Fail api call
        if (successes.length === 0) {
          throw new Error("No file could be uploaded.");
        }

        // Take the existing attachments from the `evidence` prop
        const existing =
          Array.isArray(evidence) && evidence.length
            ? evidence.map((e) => ({
                attachments_file: e.attachments_file,
                attachments_data: e.attachments_data,
              }))
            : [];

        // Combine + deduplicate by 'attachments_file'
        const combined = [...existing, ...successes].reduce((acc, item) => {
          if (!acc.find((i) => i.attachments_file === item.attachments_file)) {
            acc.push(item);
          }
          return acc;
        }, []);

        // Send to the update endpoint
        const dataToSend = {
          alias,
          interviews_id: id,
          interviews_data: { attachments: combined },
        };

        const resp = await updateInterview(dataToSend);
        // upload UI
        setInterviews(resp.data.interviews);
        closeAddNewFile();

        if (failures.length > 1) {
          enqueueSnackbar(
            `${successes.length} ${t(
              "Interview.files_uploaded_successfully"
            )}. ${failures.length} ${t("Interview.failed")}.`,
            { variant: "warning" }
          );
        } else {
          enqueueSnackbar(t("Interview.data_saved_successfully"), {
            variant: "success",
          });
        }
      } catch (error) {
        console.error("Error uploading files: ", error);
        enqueueSnackbar(t("Interview.error_uploading_files"), {
          variant: "error",
        });
      } finally {
        closeSnackbar(saving);
        setIsLoading(false);
      }
    };

    // ---- Utilities ---- //
    // for very long titles, show the desired length.
    const fileTitle = (fileName, maxLength = 25, showString = 25) => {
      if (fileName?.length > maxLength) {
        const firstPt = fileName.substring(0, showString);
        return `${firstPt}...`;
      }
      return fileName;
    };
    // Obtain the actual file from what EGFileUpload returns
    const extractFile = (entry) => (entry && entry.file ? entry.file : entry);

    //  convert bytes to MB and format
    const formatFileSize = (bytes) => {
      if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";

      const KB = 1024;
      const MB = 1024 * KB;

      if (bytes >= MB) {
        // >= 1 MB -> display in MB with 1 decimal place
        return `${(bytes / MB).toFixed(1)} MB`;
      } else {
        // < 1 MB -> display in KB as an integer
        return `${Math.round(bytes / KB)} KB`;
      }
    };

    // ----- View of the selected file ----- //
    const obtainPreviewSrc = (element) => {
      if (!element) return null;
      if (element.file_url) return element.file_url;

      const fileLike = element.file || element.blob;
      if (fileLike) {
        const prev = objectUrlMap.current.get(element);
        if (prev) URL.revokeObjectURL(prev);

        const obj = URL.createObjectURL(fileLike);
        objectUrlMap.current.set(element, obj);
        return obj;
      }
      return null;
    };
    // Open file preview
    const hdlOpenPreview = useCallback((element) => {
      setActiveElement(element);
      const src =
        element.file_url ??
        objectUrlMap.current.get(element) ??
        obtainPreviewSrc(element);
      setPreviewUrl(src);
      setOpenView(true);
    });
    // Close file preview
    const hdlClosePreview = () => {
      if (activeElement) {
        const url = objectUrlMap.current.get(activeElement);
        if (url) {
          URL.revokeObjectURL(url);
          objectUrlMap.current.delete(activeElement);
        }
      }
      setPreviewUrl(null);
      setActiveElement(null);
      setOpenView(false);
    };

    // ----- Delete a selected file ----- //
    // Open the item menu
    const handleMenuOpen = (event, element, idx) => {
      event.stopPropagation?.();
      setAnchorEl(event.currentTarget);
      setMenuIndex(idx);
      setSelectedElement(element);
    };

    // confirm deletion -> make the request and update UI
    const handleConfirmDelete = async () => {
      if (!selectedElement) return;
      const deleting = enqueueSnackbar(t("Interview.deleting"), {
        variant: "warning",
        persist: true,
      });
      setIsLoading(true);
      try {
        const targetIdx = Array.isArray(evidence)
          ? evidence.indexOf(selectedElement)
          : -1;

        if (targetIdx < 0) {
          console.warn(
            "Selected attachment not found in evidence, aborting delete",
          );
          return;
        }

        const existing = evidence.map((e) => ({
          attachments_file: e.attachments_file,
          attachments_data: e.attachments_data,
        }));

        // Remove the selected item by position so orphan entries
        // (e.g. { file_url: null } with no attachments_file) can also be deleted.
        const filtered = existing.filter((_, i) => i !== targetIdx);

        const dataToSend = {
          alias,
          interviews_id: id,
          interviews_data: { attachments: filtered },
        };

        const resp = await updateInterview(dataToSend);
        // updateInterview returns undefined when there's no active session,
        // and shows its own error toast on API failures. Bail out cleanly
        // instead of crashing on resp.data.
        if (!resp?.data?.interviews) return;

        setInterviews(resp.data.interviews);
        enqueueSnackbar(t("Interview.file_successfully_deleted"), {
          variant: "success",
        });
      } catch (error) {
        console.error("Failed to delete file:", error);
        // Avoid duplicating the toast that updateInterview already shows for
        // HTTP errors. For JS errors / network failures (no error.response),
        // surface a visible message ourselves so the user gets feedback.
        if (!error?.response) {
          enqueueSnackbar(t("Interview.error_deleting_file"), {
            variant: "error",
            preventDuplicate: true,
          });
        }
      } finally {
        setIsLoading(false);
        setOpenConfirm(false);
        setSelectedElement(null);
        closeSnackbar(deleting);
      }
    };

    // Close menu
    const handleMenuClose = () => {
      setAnchorEl(null);
      setMenuIndex(null);
    };

    // Open menu confirm
    const handleOpenConfirm = () => {
      setOpenConfirm(true);
      handleMenuClose();
    };

    // Cancel confirm
    const handleCancelConfirm = () => {
      setOpenConfirm(false);
      setSelectedElement(null);
    };

    useImperativeHandle(ref, () => ({ addNewFile }), [addNewFile]);

    const TypeIcon = ({ iconKey }) => {
      //  Map 'iconKey' to <Icon/> components
      switch (iconKey) {
        case "image":
          return <Gallery size={30} />;
        case "audio":
          return <VolumeHigh size={30} />;
        case "video":
          return <VideoSquare size={30} />;
        case "pdf":
          return <OutlineDocumentPDF size={30} />;
        case "excel":
          return <ChartSquare size={30} />;
        case "word":
          return <OutlineDocumentText size={30} />;
        case "ppt":
          return <PPTX size={30} />;
        case "archive":
          return <Folder size={30} />;
        case "document":
          return <OutlineDocument size={30} />;
        default:
          return <OutlineDocumentText2 size={30} />;
      }
    };

    return (
      <>
        {/* Upload files Modal */}
        <Dialog open={openAttachmentUploader} fullWidth maxWidth="md">
          <DialogTitle
            sx={{
              borderBottom: `1px solid ${
                theme.palette.blueGray50?.main || "#eee"
              }`,
            }}
          >
            <Typography component="span" fontWeight={600} fontSize={20}>
              {t("Interview.upload_your_evidence_files")}
            </Typography>
            <EGIconButton
              iconName="cancel_circle"
              iconColor="primary"
              onClick={closeAddNewFile}
              disabled={isLoading}
              sx={{ position: "absolute", top: 15, right: 12 }}
              iconSize={25}
            />
          </DialogTitle>
          <DialogContent>
            <Box pt={3} px={1}>
              <EGFileUpload
                title={t("Interview.evidence_files")}
                files={evidenceFiles}
                setFiles={handleSetFilesFromUpload}
                disabled={isLoading}
              />
              {/* Displays the list of files with name + size in MB and delete button */}
              <Stack
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: 1,
                  mt: evidenceFiles.length > 0 ? 1.5 : 0,
                  p: 0.5,
                  maxHeight: "25vh",
                  overflowY: "auto",
                }}
              >
                {evidenceFiles.map((entry, i) => {
                  const file = extractFile(entry);
                  return (
                    <Chip
                      key={i}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexFlow: "column nowrap",
                            gap: 0.5,
                            width: "100%",
                            py: 0.3,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontStyle="italic"
                            color="secondary"
                          >
                            <Paperclip
                              size={14}
                              color={theme.palette.secondary.main}
                            />
                            {` ${fileTitle(file.name)} (${formatFileSize(
                              file.size
                            )})`}
                          </Typography>
                          <EGBasicSelect
                            label={`${t(
                              "Interview.document_classification"
                            )} (${t("Interview.optional")}):`}
                            value={entry.purpose ?? ""}
                            onChange={(e) =>
                              updateFilePurpose(file.name, e.target.value)
                            }
                            options={typeFileOpts}
                            onClick={(e) => e.stopPropagation()}
                            disabled={isLoading}
                            sx={{ minWidth: 110, width: "100%" }}
                          />
                        </Box>
                      }
                      variant="outlined"
                      sx={{
                        boxShadow: `0 0 5px -2px ${theme.palette.common.black}`,
                        border: "transparent",
                        display: "flex",
                        width: "100%",
                        "& .MuiChip-label": { width: "100%" },
                        fontSize: 13,
                        p: 0.75,
                        height: "auto",
                        justifyContent: "space-between",
                        overflow: "hidden",
                        "& .MuiChip-deleteIcon": {
                          color: theme.palette.pink.main,
                          "&:hover": { color: "error.light" },
                        },
                      }}
                      onDelete={
                        isLoading ? undefined : () => removeFile(file.name)
                      }
                      deleteIcon={
                        <SvgIcon>
                          <TrashCan />
                        </SvgIcon>
                      }
                    />
                  );
                })}
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions sx={{ pb: 2, px: 2 }}>
            <EGButton
              label={t("Interview.save")}
              color="success"
              labelColor="white"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!evidenceFiles.length}
            />
            <EGButton
              label={t("Interview.cancel")}
              color="secondary"
              onClick={closeAddNewFile}
              loading={isLoading}
            />
          </DialogActions>
        </Dialog>

        {/* View selected file */}
        <Dialog
          open={openView}
          onClose={hdlClosePreview}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              borderBottom: `1px solid ${theme.palette.blueGray50?.main}`,
            }}
          >
            <Typography component="span" fontWeight={600} fontSize={20}>
              {activeElement?.attachments_data?.name || ""}
            </Typography>
            <EGIconButton
              iconName="cancel_circle"
              iconColor="primary"
              onClick={hdlClosePreview}
              sx={{ position: "absolute", top: 15, right: 12 }}
              iconSize={25}
            />
          </DialogTitle>
          <DialogContent dividers sx={{ position: "relative" }}>
            {activeElement
              ? (() => {
                  const fileType = detectFileType(activeElement);
                  const name = activeElement.attachments_data?.name || "";
                  const src = activeElement?.file_url;

                  // IMAGEN
                  if (fileType === "image") {
                    if (src) {
                      return (
                        <>
                          <CardMedia
                            component="img"
                            image={src}
                            loading="lazy"
                            alt={name}
                            sx={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "80vh",
                              objectFit: "contain",
                            }}
                          />
                          <Box position={"absolute"} bottom={25} right={25}>
                            <EGIconButton
                              iconName="outline_document_download"
                              color="secondary"
                              colorHover="primary"
                              component="a"
                              href={src}
                              target="_blank"
                              download={name || "image"}
                              title={t("Interview.download")}
                            />
                          </Box>
                        </>
                      );
                    }
                    return (
                      <Typography>
                        {t("Interview.no_image_available_to_display")}
                      </Typography>
                    );
                  }
                  // VIDEO
                  if (fileType === "video") {
                    if (src) {
                      return (
                        <CardMedia
                          component="video"
                          src={src}
                          controls
                          sx={{
                            width: "100%",
                            height: "auto",
                            maxHeight: "80vh",
                          }}
                        />
                      );
                    }
                    return (
                      <Typography>
                        {t("Interview.no_video_available_to_play")}
                      </Typography>
                    );
                  }

                  // AUDIO
                  if (fileType === "audio") {
                    if (src) {
                      return <AudioPlayer src={src} name={name} />;
                    }
                    return (
                      <Typography>
                        {t("Interview.no_audio_available_to_play")}
                      </Typography>
                    );
                  }
                  // PDF
                  if (fileType === "pdf" || fileType === "document") {
                    if (src) {
                      return (
                        <iframe
                          title="pdf-preview"
                          src={src}
                          style={{
                            width: "100%",
                            height: "80vh",
                            border: "none",
                          }}
                        />
                      );
                    }
                    return (
                      <Typography>
                        {t("Interview.no_PDF_available_for_preview")}
                      </Typography>
                    );
                  }

                  // Documents (Excel, PPT, world)
                  if (
                    fileType === "excel" ||
                    fileType === "ppt" ||
                    fileType === "word"
                  ) {
                    if (src) {
                      return (
                        <iframe
                          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                            src
                          )}`}
                          style={{
                            width: "100%",
                            height: "80vh",
                            border: "none",
                          }}
                        />
                      );
                    }
                  }

                  // display download link if src exists
                  return (
                    <Box>
                      {src ? (
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Link
                            href={src}
                            download={name}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {`${t("Interview.download")} ${name}`}
                          </Link>
                        </Box>
                      ) : (
                        <Typography sx={{ mt: 1 }} textAlign="center">
                          {t("Interview.no_file_available_for_preview")}
                        </Typography>
                      )}
                    </Box>
                  );
                })()
              : null}
          </DialogContent>
        </Dialog>
        {/* Delete selected file */}
        <Dialog
          open={openConfirm}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              border: `1px solid ${theme.palette.error.main}`,
              borderRadius: 2,
              px: 1,
            },
          }}
        >
          <DialogTitle
            sx={{
              py: 1.5,
              px: 0.5,
              position: "relative",
            }}
          >
            <EGIconButton
              iconName="cancel_circle"
              iconColor="primary"
              onClick={handleCancelConfirm}
              sx={{ position: "absolute", top: 12, right: 0 }}
              iconSize={25}
            />
            <Typography
              sx={{ mb: 2 }}
              component="span"
              fontWeight={600}
              fontSize={20}
              color="error"
              textAlign="center"
            >
              {`${t("Interview.delete")} '${
                selectedElement?.attachments_data?.name
              }'`}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1" fontSize={15} textAlign="center">
              {t(
                "Interview.the_file_will_be_permanently_deleted_and_cannot_be_recovered_later"
              )}
            </Typography>
            <Typography
              mt={1}
              fontWeight={600}
              textAlign="center"
              color="error"
            >
              {t("Interview.do_you_want_to_continue")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ py: 1.5, px: 2, gap: 2 }}>
            <EGButton
              label={t("Interview.delete")}
              color="error"
              onClick={handleConfirmDelete}
              loading={isLoading}
            />
            <EGButton
              label={t("Interview.cancel")}
              color="secondary"
              onClick={handleCancelConfirm}
              loading={isLoading}
            />
          </DialogActions>
        </Dialog>
        {/* Content */}
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            mx: "auto",
            width: "100%",
            height: "83vh",
            maxWidth: 800,
          }}
        >
          {evidence.length >= 1 ? (
            <ImageList sx={{ maxHeight: "76vh" }} cols={cols}>
              {evidence.map((element, i) => {
                const file = extractFile(element);
                const name = file?.attachments_data?.name || element.name;
                const fileType = detectFileType(file);
                const iconKey = getIconKey(fileType);
                const src = obtainPreviewSrc(file);
                const canShowImageThumb = fileType === "image" && !!src;
                const purpose = file?.attachments_data?.purpose;
                const hasPurposeLabel = purpose && purpose !== "other";
                const titleText = hasPurposeLabel
                  ? purposeLabel(purpose)
                  : name;
                const subtitleText = hasPurposeLabel
                  ? `${fileTitle(name, 22, 22)} · ${fileType} (${formatFileSize(
                      file?.attachments_data?.size
                    )})`
                  : `${fileType} (${formatFileSize(
                      file?.attachments_data?.size
                    )})`;

                return (
                  <ImageListItem key={i} sx={{ p: 0.3 }}>
                    <CardActionArea
                      sx={{
                        height: "100%",
                      }}
                      onClick={() => hdlOpenPreview(element)}
                    >
                      <ImageListItem>
                        {canShowImageThumb ? (
                          <CardMedia
                            component="img"
                            image={src}
                            height="125"
                            loading="lazy"
                            alt={fileTitle(name, 15, 15)}
                            sx={{
                              objectFit: "contain",
                              border: `1px solid ${theme.palette.blueGray200.main}`,
                              borderRadius: 1,
                            }}
                          />
                        ) : fileType === "pdf" && src ? (
                          <iframe
                            title="pdf-preview"
                            src={`${src}#page=1&zoom=page-fit&toolbar=0&navpanes=0&scrollbar=0`}
                            style={{
                              overflow: "hidden",
                              width: "100%",
                              height: "125px",
                              border: "none",
                              pointerEvents: "none",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: 125,
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              bgcolor: "background.paper",
                              pt: 2.5,
                              borderRadius: 2,
                              border: `1px dotted ${theme.palette.blueGray200.main}`,
                            }}
                          >
                            <TypeIcon iconKey={iconKey} />
                          </Box>
                        )}
                      </ImageListItem>

                      <ImageListItemBar
                        sx={{ height: "5.5vh" }}
                        title={
                          <Typography
                            gutterBottom
                            fontSize={11}
                            fontWeight={600}
                            textAlign="center"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "normal",
                              my: "auto",
                            }}
                          >
                            {fileTitle(titleText, 25, 25)}
                          </Typography>
                        }
                        subtitle={
                          <Typography fontSize={10} textAlign="center">
                            {subtitleText}
                          </Typography>
                        }
                        actionIcon={
                          readOnly ? undefined : (
                            <EGIconButton
                              component="span"
                              title={t("Interview.options")}
                              iconName="more_dots"
                              color="white"
                              iconColor="primary"
                              iconSize={12}
                              sx={{
                                transform: "rotate(90deg)",
                                marginRight: "8px",
                              }}
                              onClick={(e) => {
                                handleMenuOpen(e, element, i);
                                e.stopPropagation();
                              }}
                              aria-controls={
                                menuIndex === i && Boolean(anchorEl)
                                  ? `evidence-element-${i}`
                                  : undefined
                              }
                              aria-haspopup="true"
                              aria-expanded={
                                menuIndex === i && Boolean(anchorEl)
                                  ? "true"
                                  : undefined
                              }
                            />
                          )
                        }
                      />
                    </CardActionArea>

                    <Menu
                      id={`evidence-element-${menuIndex ?? ""}`}
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        horizontal: "left",
                        vertical: "center",
                      }}
                      transformOrigin={{
                        horizontal: "right",
                        vertical: "center",
                      }}
                      slotProps={{
                        list: {
                          "aria-labelledby": `evidence-element-button-${
                            menuIndex ?? ""
                          }`,
                        },
                        paper: {
                          elevation: 0,
                          sx: {
                            border: `1px solid ${theme.palette.blueGray100.main}`,
                            boxShadow: "none",
                            transform: "translateX(-6px)",
                          },
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleOpenConfirm();
                        }}
                        sx={{
                          height: 5,
                          py: 1.3,
                          mx: 0.3,
                          color: theme.palette.error.main,
                          fontWeight: 600,
                          fontSize: 12,
                          borderRadius: 2,
                        }}
                      >
                        <TrashCan
                          color={theme.palette.error.main}
                          size={12}
                          style={{ marginRight: 5 }}
                        />
                        {t("Interview.delete")}
                      </MenuItem>
                    </Menu>
                  </ImageListItem>
                );
              })}
            </ImageList>
          ) : (
            <EthiquinEmptyState
              title={`${t("Interview.there_are_no_evidence_files_yet")}...`}
              height="66vh"
              imageWidth={{ xs: "70%", md: 350 }}
              action={
                !readOnly && (
                  <EGButton
                    label={t("Interview.add_file")}
                    labelColor="black"
                    size="medium"
                    iconName="add"
                    iconColor="black"
                    iconColorHover="black"
                    iconSize={22}
                    onClick={addNewFile}
                  />
                )
              }
            />
          )}
        </Paper>
      </>
    );
  }
);

Evidence.displayName = "Evidence";
